import { useContext, useEffect, useState } from "react"
import moment from "moment"
import firebase from "firebase"
import { Copy, Trash2, Loader2, Flag, Plane, Car, TrainFront, Send, Ban, Image as ImageIcon, Link2, Check, Eye } from "lucide-react"

import { db, auth } from "../../../utils/firebase"
import { publishPublicQuote, publicQuoteUrl, setPublicQuoteStatus } from "../../../utils/quotes/public-quote"
import { ModalContext } from "../../../utils/providers/modal"
import Badge from "../../../components/ui/badge/badge"
import Loading from "../../../components/app/loading/loading"
import { CreateQuoteModal } from "./new-quote/new-quote"
import QuotePreviewModal from "../../public-quote/quote-preview"

const toMillis = (timestamp) => {
	if (!timestamp) return 0
	if (typeof timestamp.toMillis === "function") return timestamp.toMillis()
	if (timestamp.seconds || timestamp._seconds) return (timestamp.seconds || timestamp._seconds) * 1000
	return 0
}

// Copy text to the clipboard, preferring the async Clipboard API and falling
// back to a hidden-textarea + execCommand for non-secure contexts (where
// navigator.clipboard is unavailable).
const copyToClipboard = async (text) => {
	try {
		if (navigator.clipboard && window.isSecureContext) {
			await navigator.clipboard.writeText(text)
			return
		}
	} catch (err) {
		// Permission policy / blocked - fall through to the execCommand path.
	}
	const textarea = document.createElement("textarea")
	textarea.value = text
	textarea.style.position = "fixed"
	textarea.style.opacity = "0"
	textarea.style.pointerEvents = "none"
	document.body.appendChild(textarea)
	textarea.focus()
	textarea.select()
	try {
		document.execCommand("copy")
	} finally {
		document.body.removeChild(textarea)
	}
}

/**
 * "Draft quotes" section on the enquiry Quotes tab: the drafts saved from the
 * "New Quote" builder (enquiries/{id}/quotes, is_draft), shown as a 2-column
 * grid of image-forward cards rather than table rows. Each card pulls the first
 * hotel/course image and a brief summary of what the quote includes. Clicking a
 * card re-opens it in the builder; the copy / delete actions reveal on hover.
 */
export default function EnquiryDrafts({ enquiryID, reloadToken = 0, onDraftChanged, onRequestSend }) {
	const [drafts, setDrafts] = useState([])
	const [activeQuotes, setActiveQuotes] = useState([])
	const [cancelledQuotes, setCancelledQuotes] = useState([])
	const [agents, setAgents] = useState({})
	const [editingDraft, setEditingDraft] = useState(null)
	const [editingReadOnly, setEditingReadOnly] = useState(false)
	// The quote (draft / sent / cancelled) open in the client-facing preview
	// overlay, or null. The preview is display-only - nothing is published.
	const [previewQuote, setPreviewQuote] = useState(null)
	const [deletingId, setDeletingId] = useState(null)
	const [copyingId, setCopyingId] = useState(null)
	const [cancellingId, setCancellingId] = useState(null)
	// Copy-public-link state: which card is publishing its link, and which just
	// copied it (for the brief "Copied" confirmation on the button).
	const [copyingLinkId, setCopyingLinkId] = useState(null)
	const [copiedLinkId, setCopiedLinkId] = useState(null)
	// True until the first fetch resolves, so the Quotes tab shows a spinner on
	// landing rather than briefly flashing empty. Stays false on later reloads.
	const [loading, setLoading] = useState(true)

	const { showModal } = useContext(ModalContext)

	// Load this enquiry's quotes - drafts (is_draft) and sent/active ones
	// (is_active) - and split them into the two sections. Refetches whenever
	// `reloadToken` changes (bumped after a save in the builder, a send, or a
	// copy / delete here).
	useEffect(() => {
		if (!enquiryID) {
			setDrafts([])
			setActiveQuotes([])
			setCancelledQuotes([])
			setAgents({})
			setLoading(false)
			return
		}

		let cancelled = false

		const fetchQuotes = async () => {
			try {
				const [draftSnap, activeSnap, cancelledSnap] = await Promise.all([
					db.collection(`enquiries/${enquiryID}/quotes`).where("is_draft", "==", true).get(),
					db.collection(`enquiries/${enquiryID}/quotes`).where("is_active", "==", true).get(),
					db.collection(`enquiries/${enquiryID}/quotes`).where("is_cancelled", "==", true).get(),
				])

				const draftRows = draftSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
				draftRows.sort((a, b) => toMillis(b.updated || b.created) - toMillis(a.updated || a.created))

				const activeRows = activeSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
				activeRows.sort((a, b) => toMillis(b.sent_at || b.updated || b.created) - toMillis(a.sent_at || a.updated || a.created))

				const cancelledRows = cancelledSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
				cancelledRows.sort((a, b) => toMillis(b.updated || b.created) - toMillis(a.updated || a.created))

				const agentIDs = Array.from(new Set([...draftRows, ...activeRows, ...cancelledRows].map((row) => row.agent).filter(Boolean)))
				const agentDocs = await Promise.all(agentIDs.map((agentID) => db.doc(`users/${agentID}`).get().then((doc) => [agentID, doc.data()])))

				if (cancelled) return
				const agentsByID = {}
				agentDocs.forEach(([agentID, agent]) => {
					agentsByID[agentID] = agent
				})
				setAgents(agentsByID)
				setDrafts(draftRows)
				setActiveQuotes(activeRows)
				setCancelledQuotes(cancelledRows)
			} catch (err) {
				console.error("Error loading quotes:", err)
				if (!cancelled) {
					setDrafts([])
					setActiveQuotes([])
					setCancelledQuotes([])
					setAgents({})
				}
			} finally {
				if (!cancelled) setLoading(false)
			}
		}

		fetchQuotes()

		return () => {
			cancelled = true
		}
	}, [enquiryID, reloadToken])

	// Confirm via the app's in-built alert modal before deleting.
	const handleDeleteDraft = (draft) => {
		if (!enquiryID || deletingId) return
		showModal({
			type: "ALERT",
			title: "Delete this quote?",
			body: "Once deleted, this quote can't be recovered.",
			cancel: { label: "Cancel", action: () => null },
			next: { label: "Yes, delete", action: () => deleteDraft(draft) },
		})
	}

	const deleteDraft = async (draft) => {
		setDeletingId(draft.id)
		try {
			await db.doc(`enquiries/${enquiryID}/quotes/${draft.id}`).delete()
			if (onDraftChanged) onDraftChanged()
		} catch (err) {
			console.error("Could not delete draft quote:", err)
		} finally {
			setDeletingId(null)
		}
	}

	// Cancel a sent/active quote: it's not deleted, just flagged so it drops into
	// the read-only Cancelled quotes section. Confirm first via the alert modal.
	const handleCancelQuote = (quote) => {
		if (!enquiryID || cancellingId) return
		showModal({
			type: "ALERT",
			title: "Cancel this quote?",
			body: "It'll move to your cancelled quotes. You'll still be able to view it, but it can't be edited.",
			cancel: { label: "Keep quote", action: () => null },
			next: { label: "Yes, cancel", action: () => cancelQuote(quote) },
		})
	}

	const cancelQuote = async (quote) => {
		setCancellingId(quote.id)
		try {
			await db.doc(`enquiries/${enquiryID}/quotes/${quote.id}`).set({ is_active: false, is_cancelled: true }, { merge: true })
			// Take the public page offline too, so a cancelled quote can't be booked.
			if (quote.public_token) await setPublicQuoteStatus(quote.public_token, "cancelled")
			if (onDraftChanged) onDraftChanged()
		} catch (err) {
			console.error("Could not cancel quote:", err)
		} finally {
			setCancellingId(null)
		}
	}

	// Open a quote in the builder. Cancelled quotes open read-only.
	const openQuote = (quote, asReadOnly = false) => {
		setEditingReadOnly(asReadOnly)
		setEditingDraft(quote)
	}

	// Duplicate a quote into a fresh DRAFT doc (new timestamps, stamped to the
	// current agent). Copying an active/sent quote yields a clean draft, so the
	// reference + active/sent state are dropped. `public_token` is dropped too -
	// otherwise the copy would re-publish into the ORIGINAL's public_quotes doc
	// (which may already be cancelled); a fresh token is minted on first send.
	// The rest is already Firestore-safe, so it's written as-is.
	const handleCopyDraft = async (draft) => {
		if (!enquiryID || copyingId) return
		setCopyingId(draft.id)
		try {
			const { id, created, updated, reference, is_active, is_draft, sent_at, public_token, ...rest } = draft
			await db.collection(`enquiries/${enquiryID}/quotes`).add({
				...rest,
				is_draft: true,
				is_active: false,
				agent: auth.currentUser?.uid || rest.agent || null,
				created: firebase.firestore.FieldValue.serverTimestamp(),
				updated: firebase.firestore.FieldValue.serverTimestamp(),
			})
			if (onDraftChanged) onDraftChanged()
		} catch (err) {
			console.error("Could not duplicate quote:", err)
		} finally {
			setCopyingId(null)
		}
	}

	// Copy the quote's public page URL to the clipboard. Only offered on sent
	// (active) quotes, which always carry a public_token, so the URL is resolved
	// synchronously and copied within the click gesture (some browsers reject an
	// async-gesture clipboard write). Falls back to the legacy execCommand path
	// for non-secure contexts. Shows a green tick for ~2s.
	const handleCopyLink = async (quote) => {
		if (!enquiryID || copyingLinkId) return

		// Resolve the URL synchronously when we already have a token; otherwise
		// publish on the fly (rare for a sent quote, but keeps it robust).
		let url = quote.public_token ? publicQuoteUrl(quote.public_token) : ""
		try {
			if (!url) {
				setCopyingLinkId(quote.id)
				url = (await publishPublicQuote(enquiryID, quote)).url
				if (onDraftChanged) onDraftChanged()
			}
			await copyToClipboard(url)
			setCopiedLinkId(quote.id)
			setTimeout(() => setCopiedLinkId((current) => (current === quote.id ? null : current)), 2000)
		} catch (err) {
			console.error("Could not copy public quote link:", err)
		} finally {
			setCopyingLinkId(null)
		}
	}

	if (loading) {
		return (
			<div className="enquiry-quotes-loading">
				<Loading />
			</div>
		)
	}

	if (!drafts.length && !activeQuotes.length && !cancelledQuotes.length) return null

	const renderCard = (quote, isActive) => (
		<DraftCard
			key={quote.id}
			draft={quote}
			agent={agents[quote.agent]}
			isActive={isActive}
			deleting={deletingId === quote.id}
			copying={copyingId === quote.id}
			cancelling={cancellingId === quote.id}
			copyingLink={copyingLinkId === quote.id}
			copiedLink={copiedLinkId === quote.id}
			onOpen={() => openQuote(quote, false)}
			onSend={() => onRequestSend && onRequestSend(quote)}
			onPreview={() => setPreviewQuote(quote)}
			onCopy={() => handleCopyDraft(quote)}
			onCopyLink={() => handleCopyLink(quote)}
			onDelete={() => handleDeleteDraft(quote)}
			onCancel={() => handleCancelQuote(quote)}
		/>
	)

	return (
		<>
			{/* Sent / active quotes sit above the drafts in their own section. */}
			{activeQuotes.length > 0 && (
				<section className="enquiry-drafts-section">
					<h3 className="enquiry-drafts-title">Sent quotes ({activeQuotes.length})</h3>
					<div className="enquiry-drafts-grid">{activeQuotes.map((quote) => renderCard(quote, true))}</div>
				</section>
			)}

			{drafts.length > 0 && (
				<section className="enquiry-drafts-section">
					<h3 className="enquiry-drafts-title">Draft quotes ({drafts.length})</h3>
					<div className="enquiry-drafts-grid">{drafts.map((quote) => renderCard(quote, false))}</div>
				</section>
			)}

			{/* Cancelled quotes sit underneath the drafts as compact read-only rows. */}
			{cancelledQuotes.length > 0 && (
				<section className="enquiry-drafts-section">
					<h3 className="enquiry-drafts-title">Cancelled quotes ({cancelledQuotes.length})</h3>
					<div className="enquiry-cancelled-list">
						{cancelledQuotes.map((quote) => (
							<CancelledCard key={quote.id} quote={quote} onOpen={() => openQuote(quote, true)} />
						))}
					</div>
				</section>
			)}

			{/* Editing a quote re-opens it in the builder; the overlay is fixed so
			    it sits over the page regardless of where it mounts in the tree.
			    Cancelled quotes open read-only. */}
			{editingDraft && (
				<CreateQuoteModal
					seed={null}
					enquiryID={enquiryID}
					draftId={editingDraft.id}
					initialDraft={editingDraft}
					readOnly={editingReadOnly}
					onClose={() => {
						setEditingDraft(null)
						setEditingReadOnly(false)
					}}
					onSaved={() => onDraftChanged && onDraftChanged()}
					onSaveAndSend={
						editingReadOnly || !onRequestSend
							? undefined
							: (quote) => {
									setEditingDraft(null)
									setEditingReadOnly(false)
									onRequestSend(quote)
							  }
					}
				/>
			)}

			{/* Client-facing preview of a quote, built from its saved state - shows
			    exactly what the client sees, without publishing anything. */}
			{previewQuote && <QuotePreviewModal quote={previewQuote} enquiryID={enquiryID} onClose={() => setPreviewQuote(null)} />}
		</>
	)
}

// Only absolute http(s) image URLs render (matching the builder); a missing /
// broken image falls back to a placeholder tile. `className` lets the same
// component back both the tall draft-card image and the small cancelled-row
// thumbnail.
function DraftCardImage({ src, className = "enquiry-draft-card-media", iconSize = 26 }) {
	const [failed, setFailed] = useState(false)
	const usable = typeof src === "string" && /^https?:\/\//i.test(src)

	if (!usable || failed) {
		return (
			<div className={`${className} is-placeholder`}>
				<ImageIcon size={iconSize} />
			</div>
		)
	}

	return <img className={className} src={src} alt="" onError={() => setFailed(true)} />
}

const TRANSFER_LABELS = {
	airport: "Airport transfers",
	golf: "Golf transfers",
	airport_golf: "Airport and golf transfers",
}

// One-line summary of a flight basket item, e.g.
// "Return flights with Ryanair (Outbound 07:42 · Return 17:45)".
const flightSummary = (flight) => {
	const legs = flight.legs || []
	const isReturn = flight.tripType === "return" && legs.length > 1
	const carrier = (legs.find((leg) => leg.carrier) || {}).carrier || ""
	const outbound = legs[0]?.depMillis
	const inbound = isReturn ? legs[legs.length - 1]?.depMillis : 0

	const times = []
	if (outbound) times.push(`Outbound ${moment(outbound).format("HH:mm")}`)
	if (inbound) times.push(`Return ${moment(inbound).format("HH:mm")}`)

	const head = isReturn ? "Return flights" : "Flights"
	return `${head}${carrier ? ` with ${carrier}` : ""}${times.length ? ` (${times.join(" · ")})` : ""}`
}

function DraftCard({ draft, agent, isActive, deleting, copying, cancelling, copyingLink, copiedLink, onOpen, onSend, onPreview, onCopy, onCopyLink, onDelete, onCancel }) {
	const basket = Array.isArray(draft.basket) ? draft.basket : []
	const hotels = basket.filter((item) => item.type === "hotel")
	const rounds = basket.filter((item) => item.type === "round")
	const flights = basket.filter((item) => item.type === "flight")
	const transfers = basket.filter((item) => item.type === "transfer")
	const hasEuroTunnel = basket.some((item) => item.type === "eurotunnel")

	// Heading = hotel name(s); fall back to the lead course / item when a draft
	// has no hotel yet. Image mirrors the builder's first hotel/course preview.
	const hotelNames = [...new Set(hotels.map((item) => item.title).filter(Boolean))]
	const heading = hotelNames.length ? hotelNames.join(", ") : rounds[0]?.title || basket[0]?.title || "Draft quote"
	const image = hotels.find((item) => item.image)?.image || rounds.find((item) => item.image)?.image || ""

	// Courses with quantities (grouped by name, in first-seen order), e.g.
	// "1 x Royal County Down, 2 x Ardglass".
	const courseCounts = rounds.reduce((list, item) => {
		if (!item.title) return list
		const existing = list.find((entry) => entry.name === item.title)
		if (existing) existing.qty += 1
		else list.push({ name: item.title, qty: 1 })
		return list
	}, [])
	const courseLabel = courseCounts.map((entry) => `${entry.qty} x ${entry.name}`).join(", ")

	// Trip meta line - only the parts that exist. Flights / transfers get their
	// own lines below the courses (see the JSX) rather than crowding this line.
	const nights = Number(draft.nights) || 0
	const guests = Number(draft.group_size) || 0
	const trip = [
		draft.travel_millis ? moment(draft.travel_millis).format("D MMM YYYY") : null,
		nights ? `${nights} night${nights === 1 ? "" : "s"}` : null,
		guests ? `${guests} guest${guests === 1 ? "" : "s"}` : null,
	].filter(Boolean)

	// Pricing. Margin health bands match the builder: <8 red, 8-15 orange,
	// 15-18 normal, >18 green - shown only when there's a sell price.
	const totalCost = draft.totals?.total_gbp || 0
	const perPersonCost = draft.totals?.per_person_gbp || 0
	// Sell prices shown on the card. A split quote lists each per-person tier
	// (e.g. "£550pp / £620pp") rather than a blended figure; a single-price quote
	// shows the one price. Older drafts fall back to the denormalised `sell_at`.
	const sellLines = draft.sell_split && Array.isArray(draft.sell_lines) ? draft.sell_lines : []
	const splitPrices = sellLines.map((line) => Number(line.price) || 0).filter((price) => price > 0)
	const sellPrices = splitPrices.length ? splitPrices : draft.sell_at ? [Number(draft.sell_at) || 0] : []
	const margin = Number(draft.totals?.margin) || 0
	const marginClass = margin < 8 ? "is-red" : margin < 15 ? "is-orange" : margin > 18 ? "is-green" : "is-normal"

	const agentName = agent?.first_name ? `${agent.first_name} ${agent.last_name || ""}`.trim() : ""
	const savedMillis = toMillis(draft.updated || draft.created)
	const savedLine = savedMillis ? `Saved ${moment(savedMillis).format("D MMM [at] HH:mm")}${agentName ? ` by ${agentName}` : ""}` : ""

	// While copying / deleting, the card is "busy": the actions stay revealed
	// (overriding the hover-only reveal) so the spinner is visible even if the
	// pointer has moved off the card.
	const busy = copying || deleting || cancelling || copyingLink
	// Keep the actions revealed during the post-copy "Copied" tick too, so the
	// green check is visible for its full couple of seconds off-hover.
	const revealed = busy || copiedLink

	return (
		<div className={["enquiry-draft-card", revealed ? "is-busy" : ""].join(" ").trim()}>
			<button type="button" className="enquiry-draft-card-main" onClick={onOpen}>
				<div className="enquiry-draft-card-figure">
					<DraftCardImage src={image} />
					<span className="enquiry-draft-card-badge">
						{draft.reference && <Badge label={draft.reference} type="WHITE" />}
						<Badge label={isActive ? "Sent" : "Draft"} type={isActive ? "POSITIVE" : "AMBER"} />
					</span>
				</div>

				<div className="enquiry-draft-card-body">
					<div className="enquiry-draft-card-content">
						{trip.length > 0 && <p className="enquiry-draft-card-trip">{trip.join(" · ")}</p>}
						<p className="enquiry-draft-card-hotels">{heading}</p>
						{courseCounts.length > 0 && (
							<p className="enquiry-draft-card-line">
								<Flag size={13} />
								<span>{courseLabel}</span>
							</p>
						)}
						{flights.map((flight, index) => (
							<p key={`flight-${index}`} className="enquiry-draft-card-line">
								<Plane size={13} />
								<span>{flightSummary(flight)}</span>
							</p>
						))}
						{transfers.map((transfer, index) => (
							<p key={`transfer-${index}`} className="enquiry-draft-card-line">
								<Car size={13} />
								<span>{TRANSFER_LABELS[transfer.transferType] || "Transfers"}</span>
							</p>
						))}
						{hasEuroTunnel && (
							<p className="enquiry-draft-card-line">
								<TrainFront size={13} />
								<span>Euro Tunnel crossing</span>
							</p>
						)}
					</div>

					<div className="enquiry-draft-card-price">
						<div className="enquiry-draft-card-price-left">
							<div className="enquiry-draft-card-costs">
								{totalCost > 0 ? (
									<>
										<span className="enquiry-draft-card-cost">Cost £{perPersonCost.toLocaleString()}pp</span>
										<span className="enquiry-draft-card-cost is-faint">£{totalCost.toLocaleString()} total</span>
									</>
								) : (
									<span className="enquiry-draft-card-cost is-faint">No costs yet</span>
								)}
							</div>
							{savedLine && <p className="enquiry-draft-card-saved">{savedLine}</p>}
						</div>

						<div className="enquiry-draft-card-price-right">
							{sellPrices.length > 0 ? (
								<>
									<span className={["enquiry-draft-card-hero", sellPrices.length > 1 ? "is-multi" : ""].join(" ").trim()}>
										{sellPrices.map((price, index) => (
											<span className="enquiry-draft-card-hero-price" key={index}>
												{index > 0 && <span className="enquiry-draft-card-hero-sep">/</span>}
												£{Math.round(price).toLocaleString()}
												<small>pp</small>
											</span>
										))}
									</span>
									<span className="enquiry-draft-card-hero-label">{sellPrices.length > 1 ? "Sell prices" : "Sell price"}</span>
									<span className={["enquiry-draft-card-margin", marginClass].join(" ")}>{margin.toFixed(1)}% margin</span>
								</>
							) : (
								<span className="enquiry-draft-card-hero is-muted">Not priced</span>
							)}
						</div>
					</div>
				</div>
			</button>

			{/* Hidden until the card is hovered/focused, then they animate in; while
			    an action runs the icon swaps for a spinner and stays put (scss). */}
			<div className="enquiry-draft-card-actions">
				<button type="button" className="enquiry-draft-action" onClick={onSend} disabled={busy} aria-label="Send quote">
					<Send size={16} />
				</button>
				{/* Preview as the client: opens the public quote page in-app, built from
				    this quote's saved state. Display-only - nothing is published, so it's
				    offered on drafts too (which have no live public page). */}
				<button type="button" className="enquiry-draft-action" onClick={onPreview} disabled={busy} aria-label="Preview as client">
					<Eye size={16} />
				</button>
				{/* Copy public link: only on sent (active) quotes - the public page
				    isn't live for unsent drafts. */}
				{isActive && (
					<button
						type="button"
						className={["enquiry-draft-action", copiedLink ? "is-copied" : ""].join(" ").trim()}
						onClick={onCopyLink}
						disabled={busy}
						aria-label="Copy public quote link">
						{copyingLink ? <Loader2 size={16} className="enquiry-draft-action-spinner" /> : copiedLink ? <Check size={16} /> : <Link2 size={16} />}
					</button>
				)}
				<button type="button" className="enquiry-draft-action" onClick={onCopy} disabled={busy} aria-label="Duplicate quote">
					{copying ? <Loader2 size={16} className="enquiry-draft-action-spinner" /> : <Copy size={16} />}
				</button>
				{isActive ? (
					<button type="button" className="enquiry-draft-action is-cancel" onClick={onCancel} disabled={busy} aria-label="Cancel quote">
						{cancelling ? <Loader2 size={16} className="enquiry-draft-action-spinner" /> : <Ban size={16} />}
					</button>
				) : (
					<button type="button" className="enquiry-draft-action is-delete" onClick={onDelete} disabled={busy} aria-label="Delete draft">
						{deleting ? <Loader2 size={16} className="enquiry-draft-action-spinner" /> : <Trash2 size={16} />}
					</button>
				)}
			</div>
		</div>
	)
}

// Compact row card for a cancelled quote: small thumbnail, hotel(s) + courses,
// then sell price(s) + margin on the right. Clicking opens the quote read-only.
function CancelledCard({ quote, onOpen }) {
	const basket = Array.isArray(quote.basket) ? quote.basket : []
	const hotels = basket.filter((item) => item.type === "hotel")
	const rounds = basket.filter((item) => item.type === "round")

	const hotelNames = [...new Set(hotels.map((item) => item.title).filter(Boolean))]
	const heading = hotelNames.length ? hotelNames.join(", ") : rounds[0]?.title || basket[0]?.title || "Quote"
	const image = hotels.find((item) => item.image)?.image || rounds.find((item) => item.image)?.image || ""

	const courseCounts = rounds.reduce((list, item) => {
		if (!item.title) return list
		const existing = list.find((entry) => entry.name === item.title)
		if (existing) existing.qty += 1
		else list.push({ name: item.title, qty: 1 })
		return list
	}, [])
	const courseLabel = courseCounts.map((entry) => `${entry.qty} x ${entry.name}`).join(", ")

	// Sell price(s) + margin band, mirroring the draft card.
	const sellLines = quote.sell_split && Array.isArray(quote.sell_lines) ? quote.sell_lines : []
	const splitPrices = sellLines.map((line) => Number(line.price) || 0).filter((price) => price > 0)
	const sellPrices = splitPrices.length ? splitPrices : quote.sell_at ? [Number(quote.sell_at) || 0] : []
	const margin = Number(quote.totals?.margin) || 0
	const marginClass = margin < 8 ? "is-red" : margin < 15 ? "is-orange" : margin > 18 ? "is-green" : "is-normal"

	return (
		<button type="button" className="enquiry-cancelled-card" onClick={onOpen}>
			<DraftCardImage src={image} className="enquiry-cancelled-thumb" iconSize={18} />

			<div className="enquiry-cancelled-body">
				<p className="enquiry-cancelled-hotels">{heading}</p>
				{courseLabel && (
					<p className="enquiry-cancelled-courses">
						<Flag size={12} />
						<span>{courseLabel}</span>
					</p>
				)}
			</div>

			<div className="enquiry-cancelled-price">
				{sellPrices.length > 0 ? (
					<>
						<span className="enquiry-cancelled-sell">
							{sellPrices.map((price, index) => (
								<span key={index}>
									{index > 0 && <span className="enquiry-cancelled-sell-sep">/</span>}£{Math.round(price).toLocaleString()}
									<small>pp</small>
								</span>
							))}
						</span>
						<span className={["enquiry-cancelled-margin", marginClass].join(" ")}>{margin.toFixed(1)}% margin</span>
					</>
				) : (
					<span className="enquiry-cancelled-sell is-muted">Not priced</span>
				)}
			</div>
		</button>
	)
}
